import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import clsx from 'clsx';

import {
  token,
  type AutomationLevel,
  type CssVar,
  type DeltaDirection,
  type FindingSeverity,
  type GateModel,
  type GateVerdict,
  type LensState,
  type LifecycleStatus,
  type TailoringDecision,
} from '../tokens';
import { mergeStyle, type StyleWithVars } from '../internal/style';
import styles from './Badge.module.css';

/**
 * A domain status badge: a fill dot plus a word. The word is mandatory — the
 * mockups' pass/fail dots were a colour-only signal, and TOKENS.md §6.2 forbids
 * that. Discriminating on `kind` is what stops a caller from putting a gate
 * verdict colour on a lifecycle status.
 */
export type BadgeSemantics =
  | { kind: 'lifecycle'; status: LifecycleStatus }
  | { kind: 'gateVerdict'; verdict: GateVerdict }
  | { kind: 'gateModel'; model: GateModel }
  | { kind: 'decision'; decision: TailoringDecision }
  | { kind: 'finding'; severity: FindingSeverity }
  | { kind: 'automation'; level: AutomationLevel }
  | { kind: 'delta'; direction: DeltaDirection }
  | { kind: 'lens'; state: LensState };

type Spec = { readonly fill: CssVar; readonly text: CssVar; readonly label: string };

const LIFECYCLE: Record<LifecycleStatus, Spec> = {
  draft: { fill: token.domain.status.draftFill, text: token.domain.status.draftText, label: 'Draft' },
  inReview: {
    fill: token.domain.status.inReviewFill,
    text: token.domain.status.inReviewText,
    label: 'In review',
  },
  published: {
    fill: token.domain.status.publishedFill,
    text: token.domain.status.publishedText,
    label: 'Published',
  },
  blocked: {
    fill: token.domain.status.blockedFill,
    text: token.domain.status.blockedText,
    label: 'Blocked',
  },
  locked: {
    fill: token.domain.status.lockedFill,
    text: token.domain.status.lockedText,
    label: 'Edit lock',
  },
};

const VERDICT: Record<GateVerdict, Spec> = {
  pass: { fill: token.domain.gate.passFill, text: token.domain.gate.passText, label: 'Pass' },
  fail: { fill: token.domain.gate.failFill, text: token.domain.gate.failText, label: 'Fail' },
  pending: {
    fill: token.domain.gate.pendingFill,
    text: token.domain.gate.pendingText,
    label: 'Pending',
  },
  waived: { fill: token.domain.gate.waivedFill, text: token.domain.gate.waivedText, label: 'Waived' },
};

const GATE_MODEL: Record<GateModel, Spec> = {
  automated: {
    fill: token.domain.gate.automatedFill,
    text: token.domain.gate.automatedText,
    label: 'Automated check',
  },
  human: {
    fill: token.domain.gate.humanFill,
    text: token.domain.gate.humanText,
    label: 'Human approval',
  },
};

const DECISION: Record<TailoringDecision, Spec> = {
  include: {
    fill: token.domain.decision.includeFill,
    text: token.domain.decision.includeText,
    label: 'Include',
  },
  adapt: {
    fill: token.domain.decision.adaptFill,
    text: token.domain.decision.adaptText,
    label: 'Adapt',
  },
  skip: { fill: token.domain.decision.skipFill, text: token.domain.decision.skipText, label: 'Skip' },
};

const FINDING: Record<FindingSeverity, Spec> = {
  blocking: {
    fill: token.domain.finding.blocking,
    text: token.domain.finding.blockingText,
    label: 'Blocking',
  },
  warning: {
    fill: token.domain.finding.warning,
    text: token.domain.finding.warningText,
    label: 'Warning',
  },
  info: { fill: token.domain.finding.info, text: token.domain.finding.infoText, label: 'Info' },
  pass: { fill: token.domain.finding.pass, text: token.domain.finding.passText, label: 'Pass' },
};

/* Automation levels have no text token: the mix meter owns the colour, the label
   stays primary text so a three-way legend does not drift out of contrast. */
const AUTOMATION: Record<AutomationLevel, Spec> = {
  automated: {
    fill: token.domain.automation.automated,
    text: token.text.primary,
    label: 'Automated',
  },
  assisted: { fill: token.domain.automation.assisted, text: token.text.primary, label: 'Assisted' },
  manual: { fill: token.domain.automation.manual, text: token.text.primary, label: 'Manual' },
};

const DELTA: Record<DeltaDirection, Spec> = {
  improve: {
    fill: token.domain.delta.improve,
    text: token.domain.delta.improve,
    label: 'Improved',
  },
  regress: { fill: token.domain.delta.regress, text: token.domain.delta.regress, label: 'Regressed' },
  neutral: { fill: token.domain.delta.neutral, text: token.domain.delta.neutral, label: 'No change' },
};

const LENS: Record<LensState, Spec> = {
  applied: {
    fill: token.domain.lens.appliedBorder,
    text: token.domain.lens.appliedText,
    label: 'Applied',
  },
  off: { fill: token.domain.lens.offBorder, text: token.domain.lens.offText, label: 'Not applied' },
};

const resolve = (semantics: BadgeSemantics): Spec => {
  switch (semantics.kind) {
    case 'lifecycle':
      return LIFECYCLE[semantics.status];
    case 'gateVerdict':
      return VERDICT[semantics.verdict];
    case 'gateModel':
      return GATE_MODEL[semantics.model];
    case 'decision':
      return DECISION[semantics.decision];
    case 'finding':
      return FINDING[semantics.severity];
    case 'automation':
      return AUTOMATION[semantics.level];
    case 'delta':
      return DELTA[semantics.direction];
    case 'lens':
      return LENS[semantics.state];
  }
};

export type BadgeProps = BadgeSemantics &
  Omit<HTMLAttributes<HTMLSpanElement>, 'children'> & {
    /** Overrides the canonical label. The badge is never label-less. */
    children?: ReactNode;
    size?: 'sm' | 'md';
    /** Hide the dot when the badge sits in a column that already has a rail. */
    hideDot?: boolean;
  };

/**
 * The discriminant fields are component props, not DOM attributes, and a union
 * cannot be rest-destructured member by member — so they are filtered by name.
 */
const OWN_KEYS = new Set([
  'kind',
  'status',
  'verdict',
  'model',
  'decision',
  'severity',
  'level',
  'direction',
  'state',
  'children',
  'size',
  'hideDot',
  'className',
  'style',
]);

const domAttributes = (props: BadgeProps): Omit<HTMLAttributes<HTMLSpanElement>, 'children'> => {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(props)) {
    if (!OWN_KEYS.has(key)) out[key] = value;
  }
  return out as Omit<HTMLAttributes<HTMLSpanElement>, 'children'>;
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(function Badge(props, ref) {
  const { children, size = 'md', hideDot = false, className, style } = props;
  const spec = resolve(props);
  const domRest = domAttributes(props);

  const vars: StyleWithVars = {
    '--dpem-badge-fill': spec.fill,
    '--dpem-badge-text': spec.text,
  };

  return (
    <span
      {...domRest}
      ref={ref}
      data-badge={props.kind}
      className={clsx(styles.badge, styles[size], className)}
      style={mergeStyle(vars, style)}
    >
      {!hideDot && <span className={styles.dot} aria-hidden="true" />}
      <span className={styles.label}>{children ?? spec.label}</span>
    </span>
  );
});
